export type ResourceRef = {
  type: string
  id: string | number
  label?: string
}

// A user-staged reference sent with a chat message (composer @-mentions and
// "Add to chat" buttons). Unlike ResourceRef (assistant-emitted, loosely typed),
// these are user picks with a closed type set; the server whitelists against it.
// `account` refers to a Truto integrated account inside a connector — it has no
// in-app route, so `connector_id` + `integration` carry the context the model
// needs to query it.
export type ChatReference = {
  type: 'task' | 'job' | 'job_template' | 'connector' | 'trigger' | 'dashboard' | 'run' | 'job_run' | 'reference' | 'account'
  id: string
  label: string
  connector_id?: number
  integration?: string
}

// Canonical entity types that map to a real detail route. Refs reaching the UI
// arrive in several shapes: search results use singular types (`task`), while
// the AI `call_api` path derives them from the response and emits plural path
// segments (`tasks`) plus a generic `entity` fallback. Normalising here lets a
// single switch handle routing, labels, and dedup.
const TYPE_ALIASES: Record<string, string> = {
  task: 'task',
  tasks: 'task',
  run: 'run',
  runs: 'run',
  job: 'job',
  jobs: 'job',
  job_run: 'job_run',
  'job-run': 'job_run',
  job_runs: 'job_run',
  'job-runs': 'job_run',
  job_template: 'job_template',
  'job-template': 'job_template',
  job_templates: 'job_template',
  'job-templates': 'job_template',
  connector: 'connector',
  connectors: 'connector',
  trigger: 'trigger',
  triggers: 'trigger',
  dashboard: 'dashboard',
  dashboards: 'dashboard',
  api_operation: 'api_operation',
  reference: 'reference',
}

// Types that resolve to a concrete detail page (so a chip becomes a link).
const ROUTED_TYPES = new Set([
  'task',
  'run',
  'job',
  'job_run',
  'job_template',
  'connector',
  'trigger',
  'dashboard',
  'api_operation',
  'reference',
])

export function canonicalResourceType(type: string): string {
  return TYPE_ALIASES[type] ?? type
}

export function resourceRefToPath(ref: ResourceRef): string {
  const id = ref.id
  switch (canonicalResourceType(ref.type)) {
    case 'task':
      return `/tasks/${id}`
    case 'run':
      return `/runs/${id}`
    case 'job':
      return `/jobs/${id}`
    case 'job_run':
      return `/job-runs/${id}`
    case 'job_template':
      return `/job-templates/${id}`
    case 'connector':
      return `/connectors`
    case 'trigger':
      return `/triggers`
    case 'dashboard':
      return `/dashboards/${id}`
    case 'api_operation':
      return '/api/docs'
    case 'reference':
      return `/ai-references`
    default:
      return '/'
  }
}

// Whether a ref points at a real in-app destination. Generic/unknown refs have
// no detail page, so the chip should render as plain (non-interactive) text
// rather than a link that goes nowhere.
export function resourceRefHasRoute(ref: ResourceRef): boolean {
  return ROUTED_TYPES.has(canonicalResourceType(ref.type))
}

export function resourceRefLabel(ref: ResourceRef): string {
  // Humanize the canonical type for fallback labels: "job_run 34" → "job run 34".
  return ref.label ?? `${canonicalResourceType(ref.type).replace(/_/g, ' ')} ${ref.id}`
}

// Collapse references that resolve to the SAME entity into one chip. The AI
// `call_api` path commonly emits several refs for a single resource — e.g. a
// path-derived `{ type: 'tasks', id: 94 }` alongside a body-derived
// `{ type: 'entity', id: 94, label: 'delete-zendesk-records-by-account' }` —
// which are the same task. We key by canonical `type:id`, fold generic
// (`entity`/unknown) refs into the lone typed ref sharing their id, and keep
// the friendliest label. Order of first appearance is preserved.
export function dedupeResourceRefs(refs: ResourceRef[] | undefined | null): ResourceRef[] {
  if (!Array.isArray(refs) || refs.length === 0) return []

  // Pass 1: for each id, which routed (typed) canonical types appear with it.
  const routedTypesById = new Map<string, Set<string>>()
  for (const ref of refs) {
    const ct = canonicalResourceType(ref.type)
    if (!ROUTED_TYPES.has(ct)) continue
    const key = String(ref.id)
    const set = routedTypesById.get(key) ?? new Set<string>()
    set.add(ct)
    routedTypesById.set(key, set)
  }

  // Pass 2: merge into output keyed by canonical `type:id`.
  const order: string[] = []
  const merged = new Map<string, ResourceRef>()

  const mergeInto = (key: string, type: string, id: string | number, label?: string) => {
    const existing = merged.get(key)
    if (!existing) {
      merged.set(key, { type, id, ...(label ? { label } : {}) })
      order.push(key)
      return
    }
    if (!existing.label && label) existing.label = label
  }

  for (const ref of refs) {
    const ct = canonicalResourceType(ref.type)
    const label = typeof ref.label === 'string' && ref.label.trim() ? ref.label.trim() : undefined

    if (!ROUTED_TYPES.has(ct)) {
      // Generic ref: fold into the typed ref when its id maps to exactly one
      // routed type; otherwise keep it as its own chip.
      const routed = routedTypesById.get(String(ref.id))
      if (routed && routed.size === 1) {
        const onlyType = routed.values().next().value as string
        mergeInto(`${onlyType}:${ref.id}`, onlyType, ref.id, label)
        continue
      }
    }

    mergeInto(`${ct}:${ref.id}`, ct, ref.id, label)
  }

  return order.map(key => merged.get(key)!)
}

const INTERNAL_PATH = /^\/(tasks|runs|jobs|job-runs|job-templates|connectors|triggers|dashboards|data-explorer)(\/|$)/

export function isInternalAppPath(href: string): boolean {
  return INTERNAL_PATH.test(href)
}
